import React, { Suspense } from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps, Switch } from 'react-router';
import { useAppSelector } from '../../@types/redux';
import { AnimatedLoader, Screen } from '@zz2/zz2-ui';
import { IUserRight } from '../../@types/model/auth/user/userRight';
import NoRightsScreen from '../rights/NoRightsScreen';
import * as localStorageService from '../../service/localStorage/localStorageService';
import { DEFAULT_RIGHTS } from '../../appConstants';

const PrivateRoute = (props : RouteProps) : JSX.Element => {
    const session = useAppSelector(x => x.auth.session);
    const { component, ...rest } = props;

    /**
     * Check if current user is associated with a right that corresponds to the destination url.
     * @param url
     */
    const hasMenuRight = (url : string) : boolean => {
        if (!session) return false;

        const userRights : Array<IUserRight> = session.user.userRights.filter(x => x.isActive);

        if (userRights.length < 1) return false;

        const filteredUserRights : Array<IUserRight> = userRights.filter(x => x.right.url === url);

        if (filteredUserRights.length > 0) {
            return true;
        } else {
            return false;
        }
    };

    const render = (routeProps : RouteComponentProps<any>) : React.ReactNode => {
        if (session) {
            if (!component) {
                return;
            }
            const Component = component;
            if (!hasMenuRight(routeProps.match.url) && DEFAULT_RIGHTS.some(x => x !== routeProps.match.url)) {
                return <NoRightsScreen />;
            }
            return <Component {...routeProps} />;
        }
        return <Redirect to={{ pathname: '/login', state: { from: routeProps.location } }} />;
    };
    return (<Route {...rest} render={render} />);
};

{/* Home */}
const Home = React.lazy(() => import('../home/Home'));
const FrequentlyAskedQuestionScreen = React.lazy(() => import('../home/FrequentlyAskedQuestionScreen'));

{/* Map */}
const NotificationMap = React.lazy(() => import('../map/NotificationMap'));

{/* Rights */}
const RightManager = React.lazy(() => import('../rights/rightManagement/RightManager'));
const UserManager = React.lazy(() => import('../rights/userManagement/UserManager'));
const RoleManager = React.lazy(() => import('../rights/roleManagement/RoleManager'));

{/* Master Data */}
const MasterDataManager = React.lazy(() => import('../masterData/MasterDataManager'));
const AccessPointManager = React.lazy(() => import('../masterData/accessPoint/AccessPointManager'));
const AccessPointCategoryManager = React.lazy(() => import('../masterData/accessPointCategory/AccessPointCategoryManager'));
const BailApplicationManager = React.lazy(() => import('../masterData/bailApplication/BailApplicationManager'));
const BailVerdictManager = React.lazy(() => import('../masterData/bailVerdict/BailVerdictManager'));
const DivisionManager = React.lazy(() => import('../masterData/division/DivisionManager'));
const DivisionTypeManager = React.lazy(() => import('../masterData/divisionType/DivisionTypeManager'));
const EmployeeManager = React.lazy(() => import('../masterData/employee/EmployeeManager'));
const GraveVisitReasonManager = React.lazy(() => import('../masterData/graveVisitReason/GraveVisitReasonManager'));
const IncidentCategoryManager = React.lazy(() => import('../masterData/incidentCategory/IncidentCategoryManager'));
const IncidentGroupManager = React.lazy(() => import('../masterData/incidentGroup/IncidentGroupManager'));
const PowerSourceManager = React.lazy(() => import('../masterData/powerSource/PowerSourceManager'));
const RegionManager = React.lazy(() => import('../masterData/region/RegionManager'));
const SapsStationManager = React.lazy(() => import('../masterData/sapsStation/SapsStationManager'));
const GraveSiteManager = React.lazy(() => import('../masterData/graveSite/GraveSiteManager'));
const ProvinceManager = React.lazy(() => import ('../masterData/province/ProvinceManager'));
const LanguageManager = React.lazy(() => import ('../masterData/language/LanguageManager'));
const HouseBreakingEntryPointManager = React.lazy(() => import ('../masterData/houseBreakingEntryPoint/HouseBreakingEntryPointManager'));
const PoachingMethodManager = React.lazy(() => import ('../masterData/poachingMethod/PoachingMethodManager'));
const VehicleOwnershipManager = React.lazy(() => import ('../masterData/vehicleOwnership/VehicleOwnershipManager'));
const WeatherConditionManager = React.lazy(() => import ('../masterData/weatherCondition/WeatherConditionManager'));
const OfficerTypeManager = React.lazy(() => import ('../masterData/officerType/OfficerTypeManager'));
const AccreditedTrainingManager = React.lazy(() => import ('../masterData/accreditedTraining/AccreditedTrainingManager'));
const OfficerGroupManager = React.lazy(() => import ('../masterData/officerGroup/OfficerGroupManager'));
const NatureOfServiceManager = React.lazy(() => import ('../masterData/natureOfService/NatureOfServiceManager'));

{/* Observation */}
const ObservationManager = React.lazy(() => import('../observation/ObservationManager'));

{/* Occurrence */}
const OccurrenceManager = React.lazy(() => import('../occurrence/OccurrenceManager'));

{/* Incident */}
const IncidentManager = React.lazy(() => import('../incident/IncidentManager'));

{/* Access Point Event */}
const PersonAccessPointEventManager = React.lazy(() => import('../accessPointEvent/person/PersonAccessPointEventManager'));
const VehicleAccessPointEventManager = React.lazy(() => import('../accessPointEvent/vehicle/VehicleAccessPointEventManager'));

{/* Grave Visit */}
const GraveVisitManager = React.lazy(() => import('../graveVisit/GraveVisitManager'));

{/* Development */}
const LogManager = React.lazy(() => import('../devTools/LogManager'));
const ContactUsManager = React.lazy(() => import('../devTools/contactUs/ContactUsManager'));
const NewsManager = React.lazy(() => import('../devTools/news/NewsManager'));
const FrequentlyAskedQuestionManager = React.lazy(() => import('../devTools/frequentlyAskedQuestion/FrequentlyAskedQuestionManager'));

const Routes = () : React.ReactElement => {
    const selectedDefaultHome = localStorageService.getDefaultHome();
    return (
        <Suspense fallback={<Screen><AnimatedLoader/></Screen>}>
            <Switch>
                <Route
                    path={'/'} exact
                    render={() => {
                        return (
                            <Redirect
                                from={'/'}
                                to={{
                                    pathname: selectedDefaultHome?.value
                                        ? selectedDefaultHome.value.toString()
                                        : '/home' }
                                }
                            />
                        );
                    }}
                />

                <PrivateRoute exact path={'/home'} component={Home} />
                <PrivateRoute exact path={'/faq'} component={FrequentlyAskedQuestionScreen} />

                <PrivateRoute exact path={'/map'} component={NotificationMap} />

                {/* Rights */}
                <PrivateRoute exact path={'/rights/userManagement'} component={UserManager} />
                <PrivateRoute exact path={'/rights/rightsManagement'} component={RightManager} />
                <PrivateRoute exact path={'/rights/roleManagement'} component={RoleManager} />

                {/* Master Data */}
                <PrivateRoute exact path={'/masterData'} component={MasterDataManager}/>
                <PrivateRoute exact path={'/masterData/accessPoint'} component={AccessPointManager} />
                <PrivateRoute exact path={'/masterData/accessPointCategory'} component={AccessPointCategoryManager} />
                <PrivateRoute exact path={'/masterData/bailApplication'} component={BailApplicationManager} />
                <PrivateRoute exact path={'/masterData/bailVerdict'} component={BailVerdictManager} />
                <PrivateRoute exact path={'/masterData/division'} component={DivisionManager} />
                <PrivateRoute exact path={'/masterData/divisionType'} component={DivisionTypeManager} />
                <PrivateRoute exact path={'/masterData/employee'} component={EmployeeManager} />
                <PrivateRoute exact path={'/masterData/graveVisitReason'} component={GraveVisitReasonManager} />
                <PrivateRoute exact path={'/masterData/incidentCategory'} component={IncidentCategoryManager} />
                <PrivateRoute exact path={'/masterData/incidentGroup'} component={IncidentGroupManager} />
                <PrivateRoute exact path={'/masterData/powerSource'} component={PowerSourceManager} />
                <PrivateRoute exact path={'/masterData/region'} component={RegionManager} />
                <PrivateRoute exact path={'/masterData/sapsStation'} component={SapsStationManager} />
                <PrivateRoute exact path={'/masterData/graveSite'} component={GraveSiteManager} />
                <PrivateRoute exact path={'/masterData/province'} component={ProvinceManager} />
                <PrivateRoute exact path={'/masterData/language'} component={LanguageManager} />
                <PrivateRoute exact path={'/masterData/houseBreakingEntryPoint'} component={HouseBreakingEntryPointManager} />
                <PrivateRoute exact path={'/masterData/poachingMethod'} component={PoachingMethodManager} />
                <PrivateRoute exact path={'/masterData/vehicleOwnership'} component={VehicleOwnershipManager} />
                <PrivateRoute exact path={'/masterData/weatherCondition'} component={WeatherConditionManager} />
                <PrivateRoute exact path={'/masterData/officerType'} component={OfficerTypeManager} />
                <PrivateRoute exact path={'/masterData/accreditedTraining'} component={AccreditedTrainingManager} />
                <PrivateRoute exact path={'/masterData/officerGroup'} component={OfficerGroupManager} />
                <PrivateRoute exact path={'/masterData/natureOfService'} component={NatureOfServiceManager} />

                {/* Observation */}
                <PrivateRoute exact path={'/observation/transactions'} component={ObservationManager} />

                {/* Occurrence */}
                <PrivateRoute exact path={'/occurrence/transactions'} component={OccurrenceManager} />

                {/* Incident */}
                <PrivateRoute exact path={'/incident/transactions'} component={IncidentManager} />

                {/* Grave Visit */}
                <PrivateRoute exact path={'/graveVisit/transactions'} component={GraveVisitManager} />

                {/* Access Point Event */}
                <PrivateRoute exact path={'/accessPointEvent/personTransactions'} component={PersonAccessPointEventManager} />
                <PrivateRoute exact path={'/accessPointEvent/vehicleTransactions'} component={VehicleAccessPointEventManager} />

                {/* Development Tools */}
                <PrivateRoute exact path={'/development/tools'} component={LogManager} />
                <PrivateRoute exact path={'/development/contactUs'} component={ContactUsManager} />
                <PrivateRoute exact path={'/development/news'} component={NewsManager} />
                <PrivateRoute exact path={'/development/faq'} component={FrequentlyAskedQuestionManager} />
            </Switch>
        </Suspense>
    );
};

export default Routes;
